import { create } from "zustand";
import {
    AccordionEntry,
    AccordionRecommendation,
    CommitmentAccordeonState,
} from "./types";

const useCommitmentAccordeonStore = create<CommitmentAccordeonState>(
    (set, get) => ({
        accordions: [],
        isOpen: (id: string) => {
            const accords = get().accordions;
            // Retorna true se o id específico está aberto OU se 'all' está aberto
            return accords.some((item) => item.id === id) || accords.some((item) => item.id === 'all');
        },
        openAccordion: (id: string) =>
            set((state) => {
                // Se já está aberto, não faz nada
                if (state.accordions.some((item) => item.id === id)) return state;

                // Se abrindo 'all', adiciona apenas 'all' (remove outros se necessário)
                if (id === 'all') {
                    const newEntry: AccordionEntry = { id: 'all', recommendations: [] };
                    return { accordions: [newEntry] };
                }

                // Para outros ids, adiciona normalmente (mas remove 'all' se existir)
                const accordionsWithoutAll = state.accordions.filter((item) => item.id !== 'all');
                const newEntry: AccordionEntry = { id, recommendations: [] };
                return { accordions: [...accordionsWithoutAll, newEntry] };
            }),
        closeAccordion: (id: string) =>
            set((state) => {
                // Se fechando 'all', remove apenas 'all'
                if (id === 'all') {
                    return {
                        accordions: state.accordions.filter((item) => item.id !== 'all'),
                    };
                }
                // Caso contrário, remove o id específico
                return {
                    accordions: state.accordions.filter((item) => item.id !== id),
                };
            }),
        toggleAccordion: (id: string) =>
            get().isOpen(id) ? get().closeAccordion(id) : get().openAccordion(id),
        isRecommendationOpen: (accordionId: string, recommendationId: string) => {
            const accordion = get().accordions.find((item) => item.id === accordionId);
            return accordion
                ? accordion.recommendations.some((rec) => rec.id === recommendationId)
                : false;
        },
        /** Seleção única: marcar um produto troca a seleção anterior; marcar o já selecionado desmarca. */
        toggleRecommendation: (accordionId: string, recommendationId: string) =>
            set((state) => {
                const existing = state.accordions.find(
                    (item) => item.id === accordionId
                );

                const ensureAccordion: AccordionEntry =
                    existing ?? { id: accordionId, recommendations: [] };

                const recommendationExists = ensureAccordion.recommendations.some(
                    (rec) => rec.id === recommendationId
                );

                const updatedRecommendations: AccordionRecommendation[] = recommendationExists
                    ? []
                    : [{ id: recommendationId }];

                const updatedAccordion: AccordionEntry = {
                    ...ensureAccordion,
                    recommendations: updatedRecommendations,
                };

                const otherAccordions = state.accordions.filter(
                    (item) => item.id !== accordionId
                );

                return {
                    accordions: [...otherAccordions, updatedAccordion],
                };
            }),
        setRecommendations: (
            accordionId: string,
            recommendations: AccordionRecommendation[]
        ) =>
            set((state) => {
                const newEntry: AccordionEntry = {
                    id: accordionId,
                    recommendations,
                };

                const otherAccordions = state.accordions.filter(
                    (item) => item.id !== accordionId
                );

                return { accordions: [...otherAccordions, newEntry] };
            }),
        reset: () => set({ accordions: [] }),
    })
);

export { useCommitmentAccordeonStore };

